import { useEffect, useMemo, useRef, useState } from 'react';
import { MINIMUM_EVENT_HEIGHT, MONTH_CELL_ROW_GAP } from '../../../constants';

const rowHeight = MINIMUM_EVENT_HEIGHT + MONTH_CELL_ROW_GAP;

export const useVisibleEventCount = (eventCount: number, isSlotIndicatorVisible = false) => {
  const cellReference = useRef<HTMLDivElement>(null);
  const [availableHeight, setAvailableHeight] = useState(0);

  useEffect(() => {
    const element = cellReference.current;

    if (!element) {
      return;
    }

    const updateHeight = () => {
      setAvailableHeight(element.clientHeight);
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const visibleEventCount = useMemo(() => {
    if (eventCount === 0) {
      return 0;
    }

    const getRowCapacity = (height: number) => Math.floor((height + MONTH_CELL_ROW_GAP) / rowHeight);

    const slotIndicatorHeight = isSlotIndicatorVisible ? rowHeight : 0;
    const capacityWithoutMoreButton = getRowCapacity(availableHeight - slotIndicatorHeight);

    if (eventCount <= capacityWithoutMoreButton) {
      return eventCount;
    }

    const reservedHeight = slotIndicatorHeight + rowHeight;
    const eventAreaHeight = availableHeight - reservedHeight;

    if (eventAreaHeight < MINIMUM_EVENT_HEIGHT) {
      return 0;
    }

    return Math.max(0, getRowCapacity(eventAreaHeight));
  }, [availableHeight, eventCount, isSlotIndicatorVisible]);

  return { cellReference, visibleEventCount };
};
